import { type FC, useCallback, useEffect, useId, useRef, useState } from "react";
import { cn } from "../../lib/utils";
import { ClickableText } from "../../primitives/ClickableText";
import { Text, type TextColor, type TextType } from "../../primitives/Text";
import { PortaledTooltip } from "../../ui/Tooltip";

const COPIED_RESET_MS = 1200;

/** Amplitude replay ignores `data-sentry-mask`; Sentry ignores `.amp-mask`. */
const AMP_MASK_CLASS = "amp-mask";
const SENTRY_MASK_CLASS = "sentry-mask";

export type CopyableTextProps = {
	/** Text to render and copy to the clipboard. */
	value: string;
	/** Render as static text with no copy affordance (e.g. masked values). */
	disableCopy?: boolean;
	/** Semantic text color, forwarded to the underlying `Text` primitive. */
	color?: TextColor;
	/** Typographic style, forwarded to the underlying `Text` primitive. */
	type?: TextType;
	className?: string;
	testId?: string;
	trackingId?: string;
	/**
	 * Hide the value from Amplitude and Sentry session replay, and from
	 * Autocapture. Covers the overflow tooltip too, which portals outside this
	 * subtree.
	 */
	mask?: boolean;
};

/**
 * Inline value that copies itself to the clipboard on click, confirming with a transient
 * "Copied" tooltip and surfacing the full value on hover when truncated. Composes the
 * `ClickableText` primitive when copyable and the `Text` primitive when `disableCopy` is set.
 */
export const CopyableText: FC<CopyableTextProps> = ({
	value,
	disableCopy = false,
	color,
	type,
	className,
	testId = "copyable-text",
	trackingId,
	mask = false,
}) => {
	const tooltipId = useId();
	const textRef = useRef<HTMLElement | null>(null);
	const [copied, setCopied] = useState(false);
	const [showOverflow, setShowOverflow] = useState(false);
	const resetTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

	useEffect(() => () => clearTimeout(resetTimeout.current), []);

	const setRef = useCallback((el: HTMLElement | null) => {
		textRef.current = el;
	}, []);

	const handleCopy = useCallback(async () => {
		try {
			await navigator.clipboard.writeText(value);
		} catch {
			return;
		}
		setCopied(true);
		clearTimeout(resetTimeout.current);
		resetTimeout.current = setTimeout(() => setCopied(false), COPIED_RESET_MS);
	}, [value]);

	const handleMouseEnter = useCallback(() => {
		const el = textRef.current;
		if (el && el.scrollWidth > el.clientWidth) {
			setShowOverflow(true);
		}
	}, []);

	const handleMouseLeave = useCallback(() => setShowOverflow(false), []);

	const sharedProps = {
		color,
		type,
		trackingId,
		className: cn("block min-w-0 truncate", className),
		"data-testid": testId,
		"data-tooltip-id": tooltipId,
		onMouseEnter: handleMouseEnter,
		onMouseLeave: handleMouseLeave,
	};

	return (
		<div
			className="flex items-center space-x-[5px] min-w-0"
			data-amp-mask={mask ? "" : undefined}
			data-sentry-mask={mask ? "" : undefined}
		>
			{disableCopy ? (
				<Text ref={setRef} {...sharedProps}>
					{value}
				</Text>
			) : (
				<ClickableText ref={setRef} onClick={handleCopy} {...sharedProps}>
					{value}
				</ClickableText>
			)}
			<PortaledTooltip
				id={tooltipId}
				content={copied ? "Copied" : value}
				isOpen={copied || showOverflow}
				className={mask ? cn(AMP_MASK_CLASS, SENTRY_MASK_CLASS) : undefined}
			/>
		</div>
	);
};
